import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';
import { listMyCareerModes, setCareerModeEnabled, setActiveCareerMode } from '../services/careerModeService';
import { CAREER_MODES } from '../utils/careerModes';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession]             = useState(null);
  const [profile, setProfile]             = useState(null);
  // Phase C.1b — Career Mode is an EXPERIENCE preference (which of Fan/
  // Athlete/Parent-Guardian/Coach this user has enabled), never
  // authorization — see migration 037's own header. Lives alongside
  // `profile` here since AuthContext is already this app's one existing
  // profile/user-preference infrastructure (this phase's own instruction:
  // "prefer extending existing... rather than a disconnected identity
  // system"), not because it's part of the `profiles` row itself (the
  // enabled-modes LIST is its own table, profile_career_modes — see the
  // migration for why). `profile.active_career_mode` (the ACTIVE one) IS
  // a plain column on the profiles row, so it already arrives for free
  // inside `profile` via get_my_profile()'s row_to_json — no separate
  // state needed for it.
  const [careerModes, setCareerModes]     = useState([]);
  const [authLoading, setAuthLoading]     = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  // Which tab AuthModal opens on (0 = Sign In, 1 = Create Account) — lifted
  // out of AuthModal itself so a caller (e.g. RequireAuth's invite-page
  // "New to Football for Nerds?" button) can choose it before opening,
  // without duplicating the auth form. Every existing setAuthModalOpen(true)
  // call site is unaffected: it never sets this, so the modal opens on
  // whatever tab was last active — identical to AuthModal's own prior
  // behavior, since it never reset the tab on close either.
  const [authModalTab, setAuthModalTab]   = useState(0);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [viewAsTier, setViewAsTier]       = useState(null); // admin: 'basic'|'free'|'premium'|null

  // Fetch the current user's profile row. Creation is NOT this function's
  // job: the auth.users -> profiles trigger (handle_new_user, server-side,
  // SECURITY DEFINER) is the sole authoritative profile-creation path, and
  // it runs synchronously as part of the signup request itself — well
  // before any session/JWT exists for the client to call this with. There
  // used to be a client-side fallback insert here for a "no profile yet"
  // case; it's removed, not just left alone, because it could never have
  // actually worked: profiles has no INSERT RLS policy (deliberately —
  // profile creation isn't a client-side operation), so that insert always
  // silently failed (its error was never even checked) and the code fell
  // back to rendering a fabricated, never-persisted profile object instead.
  // That was worse than no profile at all. If get_my_profile() genuinely
  // comes back empty, that's a real problem to surface, not paper over —
  // so this just leaves profile null and logs it.
  const loadProfile = useCallback(async (userId, email) => {
    const { data, error } = await supabase.rpc('get_my_profile');
    if (error) {
      // eslint-disable-next-line no-console
      console.error('get_my_profile failed:', error.message);
      setProfile(null);
      return;
    }
    if (!data) {
      // eslint-disable-next-line no-console
      console.error('No profile row found for signed-in user', userId, email);
    }
    setProfile(data || null);
  }, []);

  // Phase C.1b — companion loader for the enabled-Career-Modes list.
  // Deliberately non-fatal: a failure here must never block sign-in or
  // profile loading (Career Mode is a preference, not a gate) — it just
  // falls back to "no modes enabled," the same safe empty state a
  // genuinely brand-new user already has.
  const loadCareerModes = useCallback(async () => {
    try {
      const modes = await listMyCareerModes();
      setCareerModes(modes);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('load career modes failed:', e.message);
      setCareerModes([]);
    }
  }, []);

  // Both fetches together, for the three call sites below that need
  // "the signed-in user's full profile-related state" — never changes
  // WHEN either fetch fires relative to the auth-lock timing this
  // effect's own header comments describe, only bundles a second,
  // independent, non-blocking fetch alongside the existing one.
  const loadProfileAndCareerModes = useCallback(async (userId, email) => {
    await Promise.all([loadProfile(userId, email), loadCareerModes()]);
  }, [loadProfile, loadCareerModes]);

  useEffect(() => {
    let initialised = false;

    // Listen for auth changes FIRST (catches restores + token refreshes).
    //
    // IMPORTANT: this callback must never `await` other Supabase calls
    // directly. Supabase's own client awaits every onAuthStateChange
    // subscriber callback from inside _notifyAllSubscribers(), which itself
    // runs inside _initialize()'s _acquireLock(...) — i.e. this callback
    // executes *while the client's internal session lock is held*. loadProfile()
    // calls supabase.rpc(), which needs to read the current session and so
    // tries to acquire that same lock again — a lock a single execution
    // path can't acquire twice. That nested attempt times out after the
    // client's default 5s lockAcquireTimeout and throws, which happens
    // *before* this callback reaches `setAuthLoading(false)` below — so on
    // a hard refresh (when _initialize() is genuinely doing lock-guarded
    // work restoring the session from storage), authLoading, session, and
    // profile could all stay unresolved, and nothing downstream (including
    // the athletes fetch) ever fires. Fix: defer the profile load to the
    // next tick via setTimeout(0), so it runs after this callback — and
    // the lock it's part of — has already returned.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setTimeout(async () => {
        if (s?.user) {
          await loadProfileAndCareerModes(s.user.id, s.user.email);
        } else {
          setProfile(null);
          setCareerModes([]);
        }
        if (!initialised) {
          initialised = true;
          setAuthLoading(false);
        }
      }, 0);
    });

    // Kick off session restore. Same deferral applied here defensively —
    // getSession() shares the same underlying initialization sequence as
    // the callback above, so it can be part of the same lock contention.
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (initialised) return;
      setSession(s ?? null);
      setTimeout(async () => {
        if (initialised) return; // the callback above may have already won
        if (s?.user) {
          await loadProfileAndCareerModes(s.user.id, s.user.email);
        }
        if (!initialised) {
          initialised = true;
          setAuthLoading(false);
        }
      }, 0);
    }).catch(() => {
      if (!initialised) {
        initialised = true;
        setAuthLoading(false);
      }
    });

    // Hard safety net — 8s
    const timeout = setTimeout(() => {
      if (!initialised) {
        initialised = true;
        setAuthLoading(false);
      }
    }, 8000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [loadProfileAndCareerModes]);

  // Platform admin is decided ONLY by the explicit profiles.is_platform_admin
  // flag — never by NODE_ENV, and never by profiles.plan (plan is billing/tier
  // only). There is deliberately no local-dev auto-admin bypass: this app
  // stores youth-athlete data, so local builds get the same access rules as
  // production. To test admin/platform-admin UI locally, set
  // is_platform_admin = true on your own profiles row in Supabase.
  const realIsAdmin   = Boolean(profile?.is_platform_admin);
  const realIsPremium = realIsAdmin || profile?.plan === 'nerds+' || profile?.plan === 'premium';
  const realIsFree    = Boolean(session) && !realIsPremium;
  const realIsBasic   = !session;

  const effectiveTier = realIsAdmin && viewAsTier ? viewAsTier : null;
  const isAdmin   = realIsAdmin  && !effectiveTier;
  const isPremium = effectiveTier === 'premium' ? true  : effectiveTier ? false : realIsPremium;
  const isFree    = effectiveTier === 'free'    ? true  : effectiveTier ? false : realIsFree;
  const isBasic   = effectiveTier === 'basic'   ? true  : effectiveTier ? false : realIsBasic;

  const refreshProfile = useCallback(async () => {
    if (!session?.user) return;
    await loadProfile(session.user.id, session.user.email);
  }, [session, loadProfile]);

  // Phase C.1b — refetches ONLY profile_career_modes (+ the profile row,
  // since active_career_mode lives there) — a separate function from
  // refreshProfile() above rather than folding career-mode refetching
  // into it, so every EXISTING refreshProfile()/refreshUserDoc() call
  // site elsewhere in the app keeps doing exactly one fetch, unchanged.
  const refreshCareerModes = useCallback(async () => {
    if (!session?.user) return;
    await loadProfileAndCareerModes(session.user.id, session.user.email);
  }, [session, loadProfileAndCareerModes]);

  // Thin mutator wrappers — call the RPC, then refetch so `careerModes`/
  // `profile.active_career_mode` reflect the new server-side truth rather
  // than being guessed at optimistically. Career Mode changes are rare,
  // deliberate user actions (a settings toggle, a mode switcher pick),
  // never a hot path, so a full refetch per call is the simplest correct
  // choice — no cache to keep consistent, no risk of local state drifting
  // from what set_career_mode_enabled()/set_active_career_mode() (037)
  // actually enforced server-side (e.g. disabling the active mode clears
  // it there, not here).
  const enableCareerMode = useCallback(async (mode) => {
    await setCareerModeEnabled(mode, true);
    await refreshCareerModes();
  }, [refreshCareerModes]);

  const disableCareerMode = useCallback(async (mode) => {
    await setCareerModeEnabled(mode, false);
    await refreshCareerModes();
  }, [refreshCareerModes]);

  const setActiveMode = useCallback(async (mode) => {
    await setActiveCareerMode(mode);
    await refreshCareerModes();
  }, [refreshCareerModes]);

  // Phase C.1b.1 — fixed fallback order. The naive `activeCareerMode ||
  // 'fan'` (C.1b's original version) could resolve to a mode the user
  // never enabled at all: active_career_mode is null both for a
  // genuinely mode-less user AND right after set_career_mode_enabled()
  // (037) clears it because the CURRENTLY ACTIVE mode was just disabled
  // — in the latter case, any OTHER modes the user still has enabled
  // (e.g. Guardian, while Coach was the one just turned off) must keep
  // being reflected, never silently fall all the way back to Fan.
  // Resolution order, per this phase's own explicit rule:
  //   1. stored active_career_mode, but ONLY if it's still one of the
  //      user's own enabled modes (defensive — active_career_mode should
  //      never point at a disabled mode given 037's own guarantees, but
  //      this reads the two independent pieces of state, careerModes and
  //      profile.active_career_mode, from two separate fetches, so
  //      checking rather than trusting blindly costs nothing and can
  //      never be wrong to do);
  //   2. otherwise the FIRST enabled mode in CAREER_MODES' own canonical
  //      order (utils/careerModes.js) — never database row-return order,
  //      which is not a meaningful or stable ordering to present to a
  //      user;
  //   3. otherwise 'fan' — the genuine zero-enabled-modes case.
  // Still a pure, NEVER-stored display value — profile.active_career_
  // mode itself is untouched and still read as-is (including null) by
  // anything reading `profile` directly instead of this derived value.
  const activeCareerMode = profile?.active_career_mode ?? null;
  const effectiveCareerMode =
    (activeCareerMode && careerModes.includes(activeCareerMode))
      ? activeCareerMode
      : (CAREER_MODES.find(m => careerModes.includes(m)) || 'fan');

  const currentUser = session?.user ?? null;

  return (
    <AuthContext.Provider value={{
      currentUser,
      userDoc: profile,          // keep legacy alias
      profile,
      session,
      authLoading,
      isBasic, isFree, isPremium, isAdmin,
      authModalOpen, setAuthModalOpen,
      authModalTab, setAuthModalTab,
      upgradeModalOpen, setUpgradeModalOpen,
      refreshUserDoc: refreshProfile,
      refreshProfile,
      viewAsTier, setViewAsTier,
      realIsAdmin,
      // Phase C.1b — Career Mode (experience preference, never
      // authorization; see migration 037 and utils/careerModes.js).
      careerModes,
      activeCareerMode,
      effectiveCareerMode,
      refreshCareerModes,
      enableCareerMode,
      disableCareerMode,
      setActiveMode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
